'use client';

import { useCallback, useEffect, useRef } from 'react';

// EWM-Algolia Insights — impression tracker for grid surfaces.
//
// Owns one IntersectionObserver shared across every observed element
// (cheaper than per-tile observers on a 20+ tile PLP). Each element
// needs to be visible at `threshold` for at least `dwellMs` continuous
// milliseconds before its id is reported via `onImpression`. The fired
// set is keyed by `resetKey` — when that value changes (e.g. queryID
// flips on a new search / new page / new filter), the set clears and
// already-visible elements re-fire on the next dwell window.
//
// API: returns a STABLE `observe(id, node | null)` callback suitable
// for `ref={node => observe(id, node)}` on a JSX element. On unmount
// (node === null), the element is unobserved and any pending dwell
// timer for that element is cancelled.

interface UseImpressionTrackerOptions<Id extends string> {
  /**
   * Bumping this clears the "already fired" set so the same ids will
   * fire again on their next dwell window. Pass the Algolia `queryID`
   * for PLP / search surfaces so each new search response gets a
   * fresh impression count.
   */
  resetKey: unknown;
  /**
   * IntersectionObserver visibility threshold. 0.5 means a tile must
   * be at least 50% in view to start dwelling. Default 0.5.
   */
  threshold?: number;
  /**
   * Continuous in-view time required before the impression fires, in
   * milliseconds. Default 1000.
   */
  dwellMs?: number;
  /**
   * Called once per id per reset-key window after the dwell condition
   * is met. Synchronous — the caller is responsible for batching /
   * dispatching to the analytics backend.
   */
  onImpression: (id: Id) => void;
}

interface UseImpressionTrackerResult<Id extends string> {
  observe: (id: Id, node: Element | null) => void;
}

const useImpressionTracker = <Id extends string>({
  resetKey,
  threshold = 0.5,
  dwellMs = 1000,
  onImpression,
}: UseImpressionTrackerOptions<Id>): UseImpressionTrackerResult<Id> => {
  // Ref-mirror the callback so `observe` can stay stable across renders
  // (callers attach it via `ref={node => observe(id, node)}`; a churning
  // callback would re-register every render).
  const onImpressionRef = useRef(onImpression);
  useEffect(() => {
    onImpressionRef.current = onImpression;
  }, [onImpression]);

  const observerRef = useRef<IntersectionObserver | null>(null);
  // Element → metadata. We need bi-directional lookup (entry.target → id)
  // when the observer fires, so a Map keyed by Element is the natural fit.
  const elementsRef = useRef<Map<Element, { id: Id; timer: number | null }>>(new Map());
  // Ids that have already fired for the current `resetKey`. Cleared on
  // resetKey change.
  const firedRef = useRef<Set<Id>>(new Set());

  // Lazily build the observer on first mount of an element. SSR-safe:
  // `IntersectionObserver` is undefined on the server, so any caller
  // ref-callback fired with a node from a useEffect mount runs only on
  // the client — but we still guard for older browsers / jsdom.
  const ensureObserver = useCallback((): IntersectionObserver | null => {
    if (observerRef.current) return observerRef.current;
    if (typeof IntersectionObserver === 'undefined') return null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const meta = elementsRef.current.get(entry.target);
          if (!meta) return;

          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            // Element entered (or stayed) visible. Start a dwell timer
            // if one isn't pending and the id hasn't already fired for
            // this reset-key window.
            if (firedRef.current.has(meta.id)) return;
            if (meta.timer != null) return;
            meta.timer = window.setTimeout(() => {
              meta.timer = null;
              // Double-check we haven't fired in the meantime (paranoia
              // for resetKey-change races).
              if (firedRef.current.has(meta.id)) return;
              firedRef.current.add(meta.id);
              onImpressionRef.current(meta.id);
            }, dwellMs);
          } else {
            // Element left the viewport (or dropped below threshold)
            // before the dwell completed — cancel the pending timer so
            // a quick scroll-past doesn't count.
            if (meta.timer != null) {
              window.clearTimeout(meta.timer);
              meta.timer = null;
            }
          }
        });
      },
      { threshold },
    );

    observerRef.current = observer;
    return observer;
  }, [threshold, dwellMs]);

  const observe = useCallback(
    (id: Id, node: Element | null) => {
      const observer = ensureObserver();
      const elements = elementsRef.current;

      if (node === null) {
        // React unmount path: find any element previously registered
        // for this id and detach it. (We can't index by id directly
        // because the unmount node is null — but the same id should
        // only ever map to one element at a time on a stable list.)
        elements.forEach((meta, el) => {
          if (meta.id !== id) return;
          if (meta.timer != null) window.clearTimeout(meta.timer);
          observer?.unobserve(el);
          elements.delete(el);
        });
        return;
      }

      // If this exact element was already registered, no-op (React can
      // fire the ref-callback redundantly during dev / strict mode).
      if (elements.has(node)) return;

      elements.set(node, { id, timer: null });
      observer?.observe(node);
    },
    [ensureObserver],
  );

  // Reset the fired set and re-evaluate when the upstream context
  // changes. We don't re-trigger observer callbacks manually — the
  // observer keeps firing as elements continue to intersect, and our
  // next entry check will see an empty fired-set and start a fresh
  // dwell timer.
  useEffect(() => {
    firedRef.current = new Set();
    // Cancel any in-flight dwell timers from the previous window so
    // they don't fire under stale context (e.g. previous queryID).
    elementsRef.current.forEach((meta) => {
      if (meta.timer != null) {
        window.clearTimeout(meta.timer);
        meta.timer = null;
      }
    });
  }, [resetKey]);

  // Tear down on unmount: drop the observer and any pending timers so
  // we don't leak timers / observation handles across route changes.
  // Capture the ref containers (not their `.current`) into local scope —
  // the underlying Map / observer references don't change across the
  // lifetime of the hook, so it's safe to read `.current` from inside
  // the cleanup itself.
  useEffect(() => {
    const elements = elementsRef;
    const observer = observerRef;
    return () => {
      elements.current.forEach((meta) => {
        if (meta.timer != null) window.clearTimeout(meta.timer);
      });
      elements.current.clear();
      observer.current?.disconnect();
      observer.current = null;
    };
  }, []);

  return { observe };
};

export default useImpressionTracker;
